import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BookOpen, Check, Lock, Plus, X } from 'lucide-react';
import { api } from '@/lib/api';
import { PageHeader, QueryState } from '@/components/kit/ui';
import { errMsg, unwrap } from '@/components/kit/data';
import { NotInClass } from '../shared';

interface Row { subject_id: number; name: string | null; teachers: string[]; compulsory?: boolean; mine?: boolean }
interface Book { term_id: number | null; term: string | null; can_choose: boolean; locked_reason: string | null; max_subjects: number | null; registered: Row[]; available: Row[]; class_has_list: boolean }

/** My subjects this term: what the school set for my class (compulsory or registered for me), plus electives I pick myself. */
export function SubjectsPage() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['student-subjects'], queryFn: () => api.get('/student/subjects').then((r) => unwrap<Book>(r)), retry: false });
  const b = q.data;
  const [mine, setMine] = useState<number[]>([]);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  useEffect(() => { if (b) setMine(b.registered.filter((r) => r.mine).map((r) => r.subject_id)); }, [b]);

  const save = useMutation({
    mutationFn: () => api.post('/student/subjects', { subject_ids: mine }),
    onSuccess: (r: any) => { setMsg({ ok: true, text: r.data?.message ?? 'Saved.' }); qc.setQueryData(['student-subjects'], r.data?.data); },
    onError: (e) => setMsg({ ok: false, text: errMsg(e) }),
  });

  const fixed = b?.registered.filter((r) => !r.mine) ?? [];
  const choosable = [...(b?.registered.filter((r) => r.mine) ?? []), ...(b?.available ?? [])].sort((x, y) => String(x.name).localeCompare(String(y.name)));
  const original = (b?.registered.filter((r) => r.mine).map((r) => r.subject_id) ?? []).sort().join(',');
  const dirty = [...mine].sort().join(',') !== original;
  const total = fixed.length + mine.length;
  const over = !!b?.max_subjects && total > b.max_subjects;
  const toggle = (id: number) => setMine((m) => (m.includes(id) ? m.filter((x) => x !== id) : [...m, id]));

  return (
    <div className="space-y-4 max-w-3xl pb-24">
      <PageHeader title="My subjects" subtitle={b?.term ? `${b.term}: subjects you take and who teaches them.` : 'Subjects you take and who teaches them.'} />
      <NotInClass error={q.error}>
        <QueryState q={q}>
          {b && (
            <>
              {msg && <p className={`text-sm rounded-xl px-3 py-2 ${msg.ok ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-700'}`}>{msg.text}</p>}

              <section className="bg-white rounded-2xl border border-slate-200">
                <div className="px-4 pt-3.5 pb-2 flex items-center justify-between"><h2 className="font-bold">Set by the school</h2><span className="text-xs text-slate-500">{fixed.length} subject{fixed.length === 1 ? '' : 's'}</span></div>
                {!fixed.length ? <p className="px-4 pb-4 text-sm text-slate-500">The school hasn't set any subjects for your class yet.{b.can_choose ? ' You can pick yours below.' : ''}</p> : (
                  <ul className="divide-y divide-slate-100">
                    {fixed.map((s) => (
                      <li key={s.subject_id} className="px-4 py-2.5 flex items-center gap-3">
                        <span className="w-8 h-8 rounded-lg bg-[#0A2540]/5 text-[#0A2540] grid place-items-center shrink-0"><BookOpen className="w-4 h-4" /></span>
                        <div className="min-w-0 flex-1"><p className="text-sm font-semibold text-slate-900">{s.name}</p><p className="text-xs text-slate-500 truncate">{s.teachers.length ? s.teachers.join(', ') : 'Teacher not assigned yet'}</p></div>
                        <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full shrink-0 ${s.compulsory ? 'bg-[#0A2540] text-white' : 'bg-slate-100 text-slate-600'}`}>{s.compulsory ? 'Compulsory' : 'Registered'}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </section>

              <section className="bg-white rounded-2xl border border-slate-200">
                <div className="px-4 pt-3.5 pb-2"><h2 className="font-bold">My choices</h2>
                  <p className="text-xs text-slate-500">{b.can_choose ? `Tick extra subjects you want to take${b.class_has_list ? ' from your class’s electives' : ''}.${b.max_subjects ? ` Up to ${b.max_subjects} subjects in total.` : ''}` : b.locked_reason}</p></div>
                {!b.can_choose && <p className="mx-4 mb-3 text-sm bg-slate-50 rounded-xl px-3 py-2 text-slate-600"><Lock className="w-4 h-4 inline -mt-0.5 mr-1" />{b.locked_reason}</p>}
                {!choosable.length ? <p className="px-4 pb-4 text-sm text-slate-500">No other subjects to choose from.</p> : (
                  <ul className="divide-y divide-slate-100">
                    {choosable.map((s) => {
                      const on = mine.includes(s.subject_id);
                      return (
                        <li key={s.subject_id}>
                          <button disabled={!b.can_choose} onClick={() => toggle(s.subject_id)} className={`w-full text-left px-4 py-2.5 flex items-center gap-3 disabled:cursor-default ${on ? 'bg-green-50/60' : 'hover:bg-slate-50'}`}>
                            <span className={`w-6 h-6 rounded-md border-2 grid place-items-center shrink-0 ${on ? 'bg-[#0A2540] border-[#0A2540] text-white' : 'border-slate-300'}`}>{on && <Check className="w-4 h-4" />}</span>
                            <span className="min-w-0 flex-1"><span className="block text-sm font-semibold text-slate-900">{s.name}</span><span className="block text-xs text-slate-500 truncate">{s.teachers.length ? s.teachers.join(', ') : 'Teacher not assigned yet'}</span></span>
                            {b.can_choose && (on ? <X className="w-4 h-4 text-slate-400" /> : <Plus className="w-4 h-4 text-slate-400" />)}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </section>

              {b.can_choose && dirty && (
                <div className="fixed bottom-0 inset-x-0 md:left-64 z-30 border-t border-slate-200 bg-white/95 backdrop-blur px-4 py-3">
                  <div className="max-w-3xl flex items-center gap-3">
                    <p className="flex-1 text-sm">{total} subject{total === 1 ? '' : 's'} in total{over ? <span className="text-red-600"> — more than {b.max_subjects}</span> : ''}</p>
                    <button onClick={() => setMine(b.registered.filter((r) => r.mine).map((r) => r.subject_id))} className="px-4 py-2.5 rounded-xl border border-slate-300 text-sm font-semibold">Undo</button>
                    <button disabled={save.isPending || over} onClick={() => save.mutate()} className="px-5 py-2.5 rounded-xl bg-[#0A2540] text-white text-sm font-bold disabled:opacity-40">{save.isPending ? 'Saving…' : 'Save my subjects'}</button>
                  </div>
                </div>
              )}
            </>
          )}
        </QueryState>
      </NotInClass>
    </div>
  );
}
